import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/system'; // Correct import for styled
import { useTheme } from '@mui/material/styles'; // Correct import for useTheme
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { baseUrl } from '../utils/config';
import rightSideImage from '../assets/images/respass.png';

// IMPORT YOUR CSS FILE HERE
import './ForgetPasswordUserPage.css'; // Link the CSS file

// FormContainer: This is for the white card that holds the form fields.
const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  maxWidth: 550,
  width: '100%',
  [theme.breakpoints.down('md')]: { // Adjust padding and gap for smaller screens
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: { // Further adjust for very small screens
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
  },
}));

// RightImage: This styled component will manage the background image on the right side.
const RightImage = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  backgroundImage: `url(${rightSideImage})`,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  height: '100%',
  minHeight: '600px',
  width: '100%',
  flex: 1, // Allow it to take up space on larger screens

  [theme.breakpoints.up('md')]: { // Apply flex grow, min-width ONLY on md and up
    flex: 2, // Give it a bit more space on desktop for prominence
    minWidth: '500px', // Ensure a generous minimum width on desktop
   // maxWidth: '600px', // Optional: prevent it from becoming too wide on very large screens
    height: 'auto', // Let height adjust based on content/aspect ratio
  },

  [theme.breakpoints.down('md')]: {
    height: '300px', // Fixed height when stacked (before hiding)
    marginTop: theme.spacing(4),
    display: 'none', // Explicitly hide on medium and smaller screens
  },
  [theme.breakpoints.down('sm')]: {
    height: '200px', // Adjust height for even smaller screens
    marginTop: theme.spacing(3),
  }
}));

const ResetPasswordScreen = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  useEffect(() => {
    const storedUserString = localStorage.getItem('user');

    if (storedUserString) {
      try {
        const user = JSON.parse(storedUserString);
        setUserEmail(user.email || 'N/A');
      } catch (e) {
        console.error("Failed to parse user data from localStorage", e);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setSubmitMessage({ type: '', text: '' });
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleClickShowConfirmNewPassword = () => {
    setShowConfirmNewPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };


  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters long';
      isValid = false;
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage({ type: '', text: '' });
    setLoading(true);

    if (validateForm()) {
      try {
        const userStored = JSON.parse(localStorage.getItem('user'));
        const token = userStored?.token;

        if (!token) {
          setSubmitMessage({ type: 'error', text: 'Authentication token missing. Please log in again.' });
          setLoading(false);
          return;
        }

        const response = await fetch(`${baseUrl}/api/users/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            newPassword: formData.newPassword,
            confirmNewPassword: formData.confirmNewPassword,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setSubmitMessage({ type: 'success', text: data.message || 'Password reset successfully!' });
          setFormData({
            newPassword: '',
            confirmNewPassword: '',
          });
          setErrors({});
        } else {
          setSubmitMessage({ type: 'error', text: data.message || 'Failed to reset password. Please try again.' });
        }
      } catch (error) {
        console.error('Error during password reset:', error);
        setSubmitMessage({ type: 'error', text: 'An unexpected error occurred. Please try again later.' });
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
      console.log('Form validation failed', errors);
      setSubmitMessage({ type: 'error', text: 'Please correct the highlighted errors.' });
    }
  };

  return (
    // Replaced Box with a plain div and applied the CSS class
    <div className="page-container">
      {/* Main content Box - acts as the flex container for left column (form) and right column (image) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Stack columns on xs, row on md+
          minHeight: 'calc(100vh - 64px)', // Adjust for AppBar height if present
          backgroundColor: '#f0f2f5', // This background will be seen behind content
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: theme.spacing(4), md: theme.spacing(4) }, // Add gap for spacing between columns
          p: { xs: 2, md: 3 }, // Overall padding for the main content box
          width: '100%', // Ensure it takes full width of .page-container
        }}
      >
        {/* Left Column: Content (Title + Form) */}
        <Box
          sx={{
            flex: { xs: 'none', md: 3 }, // Takes full width on xs, 3 parts on md+
            minWidth: { xs: '100%', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' }, // Center form on xs, align to start on md+
            justifyContent: 'center',
            padding: { xs: 0, md: theme.spacing(8) }, // Remove padding on xs as FormContainer has it
            maxWidth: { md: '700px' },
            width: '100%', // Ensure this inner box takes full width for centering
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              mb: { xs: 2, md: 4 }, // Adjust margin bottom
              color: '#4A148C',
              fontWeight: 'bold',
              fontFamily: 'Poppins, sans-serif',
              textAlign: { xs: 'center', md: 'left' }, // Center on xs, left on md+
              width: '100%', // Take full width to center text
            }}
          >
            Reset Your Password
          </Typography>

          <FormContainer elevation={3}>
            {userEmail && (
              <Typography variant="subtitle1" gutterBottom sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                Changing password for: **{userEmail}**
              </Typography>
            )}

            <TextField
              label="New Password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleChange}
              fullWidth
              required
              size="small" // Add size small for better mobile density
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={handleClickShowNewPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              name="confirmNewPassword"
              type={showConfirmNewPassword ? 'text' : 'password'}
              value={formData.confirmNewPassword}
              onChange={handleChange}
              fullWidth
              required
              size="small" // Add size small for better mobile density
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm new password visibility"
                      onClick={handleClickShowConfirmNewPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {submitMessage.text && (
              <Alert severity={submitMessage.type} sx={{ mt: 2 }}>
                {submitMessage.text}
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              sx={{
                mt: 2,
                py: 1.5,
                width: { xs: '100%', sm: 'auto' }, // Full width on xs, auto on sm+
                alignSelf: { xs: 'center', sm: 'flex-start' }, // Center button on xs, left on sm+
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </FormContainer>
        </Box>

        {/* Right Column: Image - This will be hidden on md and smaller by RightImage's styled component */}
        <RightImage sx={{ flex: { xs: 'none', md: 2 } }} />
      </Box>
    </div>
  );
};

export default ResetPasswordScreen;